import { Avatar, Button, Divider, Flex, Popconfirm, Tag, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import type {
  LeadMatch,
  MatchReason
} from "../../../Leads/api/services.types.ts"
import { type CustomerMatchCandidate, displayNameOf } from "./customer-match.ts"
import { type UnifiedDuplicateMatch } from "./duplicate-match.ts"
import {
  REASON_COLOUR,
  REASON_LABEL,
  isReachable,
  matchAgeLabel,
  matchStateLabel
} from "./lead-match-vocabulary.ts"

interface LeadWizardDuplicateRowProps {
  match: UnifiedDuplicateMatch
  busy?: boolean
  onUseExisting: (candidate: CustomerMatchCandidate) => void
  onOpenExisting: (lead: LeadMatch) => void
  onCreateAnyway: () => void
}

function initialsOf(candidate: CustomerMatchCandidate): string {
  return (
    [candidate.first_name, candidate.last_name]
      .filter(Boolean)
      .map((part) => part?.trim().charAt(0).toUpperCase())
      .join("")
      .slice(0, 2) || "?"
  )
}

/** The customer's own badges: both, when phone and email both matched. */
function CustomerBadges({ match }: { match: UnifiedDuplicateMatch }) {
  const { t } = useTranslation()
  if (match.kind === "lead") return null

  const reasons: MatchReason[] = []
  if (match.matchedFields.email) reasons.push("email")
  if (match.matchedFields.phone) reasons.push("phone")
  if (!reasons.length) reasons.push(match.customer.reason)

  return (
    <>
      {reasons.map((reason) => (
        <Tag key={reason} color={REASON_COLOUR[reason]}>
          {t(REASON_LABEL[reason])}
        </Tag>
      ))}
    </>
  )
}

function LeadInfoLine({ lead }: { lead: LeadMatch }) {
  const { t } = useTranslation()
  return (
    <Typography.Text type={"secondary"} className={"text-xs"}>
      {[
        matchStateLabel(lead.match_state, t),
        lead.owner_name && t("leadMatch.owner", { name: lead.owner_name }),
        matchAgeLabel(lead.created, t)
      ]
        .filter(Boolean)
        .join(" · ")}
    </Typography.Text>
  )
}

function LeadActions({
  lead,
  onOpenExisting,
  onCreateAnyway
}: {
  lead: LeadMatch
  onOpenExisting: (lead: LeadMatch) => void
  onCreateAnyway: () => void
}) {
  const { t } = useTranslation()
  return (
    <Flex align={"center"} gap={"small"}>
      {isReachable(lead) && (
        <Popconfirm
          title={t("leadWizard.leadDuplicate.confirmTitle")}
          description={t("leadWizard.leadDuplicate.confirmBody")}
          okText={t("leadWizard.leadDuplicate.confirmOk")}
          cancelText={t("leadWizard.leadDuplicate.confirmCancel")}
          onConfirm={() => onOpenExisting(lead)}
        >
          <Button size={"small"}>
            {t("leadWizard.leadDuplicate.openExisting")}
          </Button>
        </Popconfirm>
      )}
      {/* Two ways out on every row and never a third: a row the caller cannot
          open still offers this one. */}
      <Button size={"small"} type={"link"} onClick={onCreateAnyway}>
        {t("leadWizard.leadDuplicate.createAnyway")}
      </Button>
    </Flex>
  )
}

/**
 * The merged row is the one place all three exits meet: the same record can be
 * opened by its lead, taken as the customer for a fresh lead, or bypassed
 * outright — so all three sit as siblings in one action row rather than
 * splitting across a lead-shaped row and a customer-shaped row the way a
 * stacked pair of warnings used to.
 */
function MergedRowActions({
  customer,
  lead,
  busy,
  onUseExisting,
  onOpenExisting,
  onCreateAnyway
}: {
  customer: CustomerMatchCandidate
  lead: LeadMatch
  busy?: boolean
  onUseExisting: (candidate: CustomerMatchCandidate) => void
  onOpenExisting: (lead: LeadMatch) => void
  onCreateAnyway: () => void
}) {
  const { t } = useTranslation()
  return (
    <Flex align={"center"} justify={"space-between"} gap={"small"} wrap>
      <Flex align={"center"} gap={"small"} wrap>
        {isReachable(lead) && (
          <Popconfirm
            title={t("leadWizard.leadDuplicate.confirmTitle")}
            description={t("leadWizard.leadDuplicate.confirmBody")}
            okText={t("leadWizard.leadDuplicate.confirmOk")}
            cancelText={t("leadWizard.leadDuplicate.confirmCancel")}
            onConfirm={() => onOpenExisting(lead)}
          >
            <Button size={"small"}>
              {t("leadWizard.leadDuplicate.openExisting")}
            </Button>
          </Popconfirm>
        )}
        {/* The create call refuses a suspended customer outright, so offering
            the link would only spend the rest of the wizard on a guaranteed
            rejection. */}
        <Button
          size={"small"}
          loading={busy}
          disabled={!!customer.is_suspended}
          onClick={() => onUseExisting(customer)}
        >
          {t("leadWizard.duplicate.useExisting")}
        </Button>
      </Flex>
      <Button size={"small"} type={"link"} onClick={onCreateAnyway}>
        {t("leadWizard.leadDuplicate.createAnyway")}
      </Button>
    </Flex>
  )
}

export function LeadWizardDuplicateRow({
  match,
  busy,
  onUseExisting,
  onOpenExisting,
  onCreateAnyway
}: LeadWizardDuplicateRowProps) {
  const { t } = useTranslation()

  if (match.kind === "lead") {
    return (
      <Flex
        key={match.key}
        align={"center"}
        justify={"space-between"}
        gap={"small"}
        wrap
      >
        <Flex vertical>
          <Typography.Text strong>
            {match.lead.lead_name ?? t("leadMatch.hiddenLead")}
          </Typography.Text>
          <LeadInfoLine lead={match.lead} />
        </Flex>

        <Flex align={"center"} gap={"small"}>
          <Tag color={REASON_COLOUR[match.lead.reason]}>
            {t(REASON_LABEL[match.lead.reason])}
          </Tag>
          <LeadActions
            lead={match.lead}
            onOpenExisting={onOpenExisting}
            onCreateAnyway={onCreateAnyway}
          />
        </Flex>
      </Flex>
    )
  }

  if (match.kind === "merged") {
    const { customer, lead } = match
    const name = displayNameOf(customer)

    return (
      <Flex
        vertical
        gap={"small"}
        className={"rounded-lg border border-black/10 px-3 py-2"}
      >
        <Flex gap={"middle"} align={"stretch"} wrap>
          <Flex gap={"small"} align={"flex-start"} className={"min-w-0 flex-1"}>
            <Avatar>{initialsOf(customer)}</Avatar>
            <Flex vertical gap={2} className={"min-w-0"}>
              <Typography.Text strong>
                {name || t("leadWizard.duplicate.unnamed")}
              </Typography.Text>
              {customer.email && (
                <Typography.Text type={"secondary"} className={"text-xs"}>
                  {customer.email}
                </Typography.Text>
              )}
              {customer.phone && (
                <Typography.Text type={"secondary"} className={"text-xs"}>
                  {customer.phone}
                </Typography.Text>
              )}
              <Flex gap={"small"} wrap>
                <CustomerBadges match={match} />
                {customer.is_suspended && (
                  <Tag color={"red"}>{t("leadWizard.duplicate.suspended")}</Tag>
                )}
                {customer.is_incomplete && (
                  <Tag>{t("leadWizard.duplicate.incomplete")}</Tag>
                )}
              </Flex>
              {customer.is_suspended && (
                <Typography.Text type={"danger"} className={"text-xs"}>
                  {t("leadWizard.duplicate.suspendedHint")}
                </Typography.Text>
              )}
            </Flex>
          </Flex>

          <Divider
            type={"vertical"}
            className={"hidden h-auto self-stretch sm:block"}
          />

          <Flex vertical gap={2} className={"min-w-0 flex-1"}>
            <Typography.Text type={"secondary"} className={"text-xs font-medium"}>
              {t("leadWizard.leadDuplicate.lastLead")}
            </Typography.Text>
            <Typography.Text strong>
              {lead.lead_name ?? t("leadMatch.hiddenLead")}
            </Typography.Text>
            <LeadInfoLine lead={lead} />
          </Flex>
        </Flex>

        <MergedRowActions
          customer={customer}
          lead={lead}
          busy={busy}
          onUseExisting={onUseExisting}
          onOpenExisting={onOpenExisting}
          onCreateAnyway={onCreateAnyway}
        />
      </Flex>
    )
  }

  const { customer } = match
  const name = displayNameOf(customer)
  const handles = [customer.email, customer.phone].filter(Boolean).join(" · ")

  return (
    <Flex
      gap={"small"}
      align={"center"}
      justify={"space-between"}
      wrap
      className={"rounded-lg border border-black/10 px-3 py-2"}
    >
      <Avatar>{initialsOf(customer)}</Avatar>
      <Flex vertical gap={2} className={"min-w-0 flex-1"}>
        <Flex gap={"small"} align={"center"} wrap>
          <Typography.Text strong>
            {name || t("leadWizard.duplicate.unnamed")}
          </Typography.Text>
          <CustomerBadges match={match} />
          {customer.is_suspended && (
            <Tag color={"red"}>{t("leadWizard.duplicate.suspended")}</Tag>
          )}
          {customer.is_incomplete && (
            <Tag>{t("leadWizard.duplicate.incomplete")}</Tag>
          )}
          {typeof customer.project_count === "number" && (
            <Tag>
              {t("leadWizard.duplicate.projects", {
                count: customer.project_count
              })}
            </Tag>
          )}
        </Flex>
        {handles && (
          <Typography.Text type={"secondary"} className={"text-xs"}>
            {handles}
          </Typography.Text>
        )}
        {customer.is_suspended && (
          <Typography.Text type={"danger"} className={"text-xs"}>
            {t("leadWizard.duplicate.suspendedHint")}
          </Typography.Text>
        )}
      </Flex>

      {/* The create call refuses a suspended customer outright, so offering the
          link would only spend the rest of the wizard on a guaranteed rejection. */}
      <Button
        size={"small"}
        loading={busy}
        disabled={!!customer.is_suspended}
        onClick={() => onUseExisting(customer)}
        className={"min-h-9"}
      >
        {t("leadWizard.duplicate.useExisting")}
      </Button>
    </Flex>
  )
}
